import { useEffect, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Flex,
  Select,
  Switch,
  Icon,
  Text,
  Counter,
  Input
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import AdditionalTableFieldToggleMenu from "../SettingsModal/ChartSettings/DataSourceConfiguration/AdditionalTableFieldToggleMenu";
import SectionPrompt from "../../Composites/SectionPrompt/SectionPrompt";
import SectionCard, { SectionCardGroup } from "../../Composites/SectionCard/SectionCard";
import { UpdateChartMetadata } from "@/Utils/Promises";
import { getPointInTime } from "../ChartExplorerModal/PointInTimeExplorer/PointInTimeExplorer.utils";
import { getLastArchiveDateLabel } from "../ChartExplorerModal/PointInTimeExplorer/PointInTimeStatus";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import {
  kReservedFields,
  kDayList,
  enumFeaturesList,
  kPITMaxArchiveAgeDays
} from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";

const frequencyOptions = ["Daily", "Weekly", "Bi-Weekly", "Monthly", "Quarterly", "Annually"];
const monthlyOptions = ["First Day", "Last Day"];
const cleanUpOptions = ["Days", "Months", "Years"];
// Longest retention the system honours, per unit the selector offers. Matches
// _configuredCleanupDelta, which counts a month as 30 days.
const cleanUpMaxValues = {
  Days: kPITMaxArchiveAgeDays,
  Months: Math.floor(kPITMaxArchiveAgeDays / 30),
  Years: Math.floor(kPITMaxArchiveAgeDays / 365)
};

// Module level: defined inside the modal, it remounted every render and dropped keyboard focus
const SwitchWrapper = ({ checked, onChange, option, label, id, subkey = null, dataTestId }) => {
  const handleSwitchChange = event => {
    onChange(option, subkey, event);
  };
  return (
    <Switch
      id={id}
      label={label}
      checked={checked}
      onChange={handleSwitchChange}
      labelPlacement={"left"}
      data-testid={dataTestId}
    />
  );
};

export const PointInTimeModal = ({
  show,
  onClose,
  onUpdate,
  chartConfig,
  contentOnly,
  testIdPrefix
}) => {
  const [clonedMetadata, setClonedMetadata] = useState(chartConfig);

  useEffect(() => {
    setClonedMetadata(chartConfig);
  }, [chartConfig]);

  const pitConfig = getPointInTime(clonedMetadata);
  const freqObj = pitConfig?.frequency;

  const UpdatePIT = updatedPIT => {
    let updatedMetadata = { ...clonedMetadata };
    updatedMetadata.pointintime = updatedPIT;
    setClonedMetadata({ ...updatedMetadata });
    if (contentOnly) onUpdate(updatedMetadata, "pointintime");
  };

  const HandleChange = (key, subkey = null, value) => {
    let updatedPIT = pitConfig || {};
    switch (key) {
      case "enabled":
        updatedPIT[key] = value;
        break;
      case "frequency":
        updatedPIT[key] = { ...updatedPIT[key], [subkey]: value };
        break;
      case "deltafieldinclude":
        if (!updatedPIT[key]) updatedPIT[key] = [];
        const fieldIdx = updatedPIT[key].indexOf(value);
        if (fieldIdx != -1)
          updatedPIT[key] = [
            ...updatedPIT[key].slice(0, fieldIdx),
            ...updatedPIT[key].slice(fieldIdx + 1)
          ];
        else updatedPIT[key] = [...updatedPIT[key], value];
        break;
      case "archiveCleanup":
        updatedPIT[key] = { ...updatedPIT[key], [subkey]: value };
        break;
    }

    UpdatePIT(updatedPIT);
  };

  const HandleSavePIT = async () => {
    const { success } = await UpdateChartMetadata(
      clonedMetadata.ID,
      JSON.stringify({ pointintime: { ...clonedMetadata.pointintime } })
    );
    if (success) {
      onClose();
      onUpdate(clonedMetadata);
    }
  };

  const HandleEmailInput = (idx, value) => {
    const notificationVal = value + ",1";
    let updatedPIT = pitConfig || {};
    const newDeltaList = [...updatedPIT.deltalist];
    newDeltaList[idx] = notificationVal;
    updatedPIT.deltalist = newDeltaList;
    UpdatePIT(updatedPIT);
  };

  const AddDeltaEmail = () => {
    let updatedPIT = pitConfig || {};
    const newDeltaList = updatedPIT?.deltalist ? [...updatedPIT.deltalist] : [];
    updatedPIT["deltalist"] = [...newDeltaList, ""];
    UpdatePIT(updatedPIT);
  };

  const DeleteDeltaEmail = idx => {
    const updatedMetadata = { ...clonedMetadata };
    const updatedPIT = pitConfig || {};
    const deltaList = updatedPIT.deltalist;
    deltaList.splice(idx, 1);
    updatedPIT.deltaList = [...deltaList];
    UpdatePIT(updatedPIT);
  };

  const BuildFreqSelectOptions = (freqList, key, subkey) => {
    let options = [];
    for (const idx in freqList) {
      const freqOption = {
        label: appstr(freqList[idx]),
        value: freqList[idx],
        clickHandler: e => HandleChange(key, subkey, e.value)
      };

      options.push(freqOption);
    }
    return options;
  };

  const GetValidDeltaFields = (fields, formulas) => {
    let invalidHeaders = kReservedFields;
    if (formulas)
      invalidHeaders = invalidHeaders.concat(
        formulas.map(a => {
          return a["name"];
        })
      );

    return fields?.filter(field => {
      return invalidHeaders.indexOf(field) < 0;
    });
  };

  const BuildDeltaFieldsOptions = validFields => {
    let options = [];

    for (const idx in validFields) {
      const fieldOption = {
        label: validFields[idx],
        value: validFields[idx]
      };

      options.push(fieldOption);
    }

    return options;
  };

  const GetDayOptions = freq => {
    switch (freq) {
      case "Weekly":
      case "Bi-Weekly":
        return kDayList;
      case "Monthly":
      case "Quarterly":
        return monthlyOptions;
    }
  };

  let content = (
    <Flex direction={"column"}>
      <DialogContent dividers={true}>
        <SectionPrompt title={appstr("scheduleoptions")}>
          <Flex direction={"column"} gap={"16px"}>
            <Flex direction={"row"} justifyContent={"space-between"}>
              <SwitchWrapper
                checked={pitConfig?.enabled}
                option={getFeatureAccess(enumFeaturesList.PITAutoArchive) ? "enabled" : "disabled"}
                label={appstr("automaticarchive")}
                id={"pit-auto-switch"}
                onChange={HandleChange}
                dataTestId={testIdPrefix ? `${testIdPrefix}-auto-archive-toggle` : undefined}
              />
              <Flex direction={"row"} gap={"8px"}>
                <Text modifier={"bold"} size={"small"}>
                  {`${appstr("lastarchive")}:`}
                </Text>
                <Text size={"small"}>{getLastArchiveDateLabel(pitConfig)}</Text>
              </Flex>
            </Flex>
            <Flex
              direction={"row"}
              justifyContent={contentOnly ? "" : "space-between"}
              alignItems={"flex-end"}
              gap={"8px"}
            >
              <div style={contentOnly ? { width: "25%" } : { width: "100%" }}>
                <Select
                  id={"pit-freq-sel"}
                  inputOnly={false}
                  label={appstr("frequency")}
                  listWidth={"medium"}
                  selectSize={"medium"}
                  value={freqObj?.period}
                  options={BuildFreqSelectOptions(frequencyOptions, "frequency", "period")}
                  dataTestId={testIdPrefix ? `${testIdPrefix}-frequency-select` : undefined}
                />
              </div>
              <div style={contentOnly ? { width: "25%" } : { width: "100%" }}>
                <Select
                  id={"pit-day-sel"}
                  listWidth={"medium"}
                  selectSize={"medium"}
                  value={freqObj?.day}
                  placeholder={appstrp("Select", "...")}
                  options={BuildFreqSelectOptions(
                    GetDayOptions(freqObj?.period),
                    "frequency",
                    "day"
                  )}
                  disabled={
                    !freqObj?.period ||
                    freqObj?.period === "Daily" ||
                    freqObj?.period === "Annually"
                  }
                  dataTestId={testIdPrefix ? `${testIdPrefix}-frequency-day-select` : undefined}
                />
              </div>
            </Flex>
          </Flex>
        </SectionPrompt>
      </DialogContent>

      <DialogContent dividers={true}>
        <SectionPrompt
          title={appstr("deltareportconfiguration")}
          description={appstr("Select fields included in delta report")}
        >
          <Flex width={contentOnly ? "25%" : "50%"}>
            <AdditionalTableFieldToggleMenu
              placeholder={appstr("Select fields")}
              inputOnly={true}
              dataTestId={testIdPrefix ? `${testIdPrefix}-delta-fields-select` : undefined}
              selectedOptions={pitConfig?.deltafieldinclude || []}
              allOptions={
                clonedMetadata
                  ? BuildDeltaFieldsOptions(
                      GetValidDeltaFields(clonedMetadata.headers, clonedMetadata.formulas)
                    )
                  : []
              }
              onChange={e => HandleChange("deltafieldinclude", null, e.value.value)}
              selectProps={{
                popoverProps: {
                  flipOptions: {
                    mainAxis: false
                  }
                }
              }}
            />
          </Flex>
        </SectionPrompt>
      </DialogContent>

      <DialogContent dividers={true}>
        <SectionPrompt
          title={appstr("Automatic delta report notifications")}
          description={appstr("Enter email address(es) for notification")}
        >
          <Flex direction={"column"} gap={"8px"}>
            {pitConfig?.deltalist &&
              pitConfig.deltalist.map((item, index) => {
                return (
                  <Flex direction={"row"} gap={"8px"} alignItems={"center"}>
                    <div style={contentOnly ? { width: "25%" } : { width: "75%" }}>
                      <Input
                        key={`pit-email-input-${index}`}
                        type={"text"}
                        size={"small"}
                        value={item.split(",")[0]}
                        onChange={e => HandleEmailInput(index, e.target.value)}
                        dataTestId={
                          testIdPrefix ? `${testIdPrefix}-delta-email-input-${index}` : undefined
                        }
                      />
                    </div>
                    <Flex>
                      <Button
                        size={"small"}
                        iconOnly={true}
                        modifier="tertiary"
                        color="error"
                        iconProps={{ name: "trash" }}
                        onClick={() => DeleteDeltaEmail(index)}
                        dataTestId={
                          testIdPrefix ? `${testIdPrefix}-delta-email-delete-${index}` : undefined
                        }
                      />
                    </Flex>
                  </Flex>
                );
              })}
            <Flex>
              <Button
                size={"small"}
                modifier="secondary"
                onClick={() => AddDeltaEmail()}
                disabled={!getFeatureAccess(enumFeaturesList.PITDeltaReports)}
                dataTestId={testIdPrefix ? `${testIdPrefix}-delta-email-add` : undefined}
              >
                {appstr("Add more")}
              </Button>
            </Flex>
          </Flex>
        </SectionPrompt>
      </DialogContent>

      <DialogContent dividers={!contentOnly}>
        <Flex direction={"column"} padding={contentOnly ? "12px 0px 0px" : null}>
          <SectionPrompt title={appstr("Archive Cleanup")}>
            <Flex direction={"column"} gap={"16px"}>
              <SwitchWrapper
                checked={pitConfig?.archiveCleanup?.enabled}
                option={"archiveCleanup"}
                subkey={
                  getFeatureAccess(enumFeaturesList.PITArchiveCleanup) ? "enabled" : "disabled"
                }
                label={appstr("Enable automatic clean-up")}
                id={"pit-cleanup-switch"}
                onChange={HandleChange}
                dataTestId={testIdPrefix ? `${testIdPrefix}-cleanup-toggle` : undefined}
              />
              {pitConfig?.archiveCleanup?.enabled && (
                <>
                  <Text size={"xsmall"}>{`${appstr("Older Than")}:`}</Text>
                  <Flex direction={"row"} gap={"12px"}>
                    <Counter
                      onChange={newValue => HandleChange("archiveCleanup", "count", newValue)}
                      currentValue={pitConfig?.archiveCleanup?.count || 1}
                      defaultValue={1}
                      minValue={1}
                      maxValue={
                        cleanUpMaxValues[pitConfig?.archiveCleanup?.period] ?? kPITMaxArchiveAgeDays
                      }
                      size={"small"}
                      dataTestIdPrefix={testIdPrefix ? `${testIdPrefix}-cleanup-count` : undefined}
                    />
                    <Flex width={contentOnly ? "25%" : "50%"}>
                      <Select
                        id={"pit-cleanup-sel"}
                        listWidth={"medium"}
                        selectSize={"medium"}
                        value={pitConfig?.archiveCleanup?.period}
                        options={BuildFreqSelectOptions(cleanUpOptions, "archiveCleanup", "period")}
                        placeholder={appstrp("Select", "...")}
                        dataTestId={
                          testIdPrefix ? `${testIdPrefix}-cleanup-period-select` : undefined
                        }
                      />
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </SectionPrompt>
        </Flex>
      </DialogContent>
    </Flex>
  );

  return contentOnly ? (
    <SectionCardGroup>
      <SectionCard>{content}</SectionCard>
    </SectionCardGroup>
  ) : (
    <CentralizedModalContainer show={show}>
      <Modal
        id={"sso-edit-modal"}
        show={show}
        centerOnScreen={true}
        fullScreen={false}
        customStyle={{ padding: 0, width: "640px" }}
      >
        <DialogTitle divider={true} onClose={onClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"stopwatch"} />
            {appstr("Point In Time")}
          </span>
        </DialogTitle>

        {content}

        <DialogActions>
          <DialogActions.Tertiary
            onClick={onClose}
            dataTestId={testIdPrefix ? `${testIdPrefix}-cancel-btn` : undefined}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={() => HandleSavePIT()}
            dataTestId={testIdPrefix ? `${testIdPrefix}-save-btn` : undefined}
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
